import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronLeft, ChevronRight, UserCheck, UserX, Crown, RotateCcw,
  X, Users, ShieldCheck, WalletCards, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingAction, setPendingAction] = useState(null);
  const limit = 25;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit), search });
      const data = await apiGet(`${API_ROUTES.admin.users}?${params}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleActive = async (userId) => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.users}/${userId}/toggle-active`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`User ${data.isActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const changePlan = async (userId, plan) => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.users}/${userId}/plan`, {
        method: 'PATCH',
        body: { plan },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Plan changed to ${plan}`);
      fetchUsers();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const resetPassword = async (userId) => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.users}/${userId}/reset-password`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Password reset initiated');
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const confirmUserAction = async () => {
    if (!pendingAction) return;
    const { type, user } = pendingAction;
    if (type === 'toggle') await toggleActive(user.id);
    if (type === 'plan') await changePlan(user.id, user.plan === 'pro' ? 'free' : 'pro');
    if (type === 'reset') await resetPassword(user.id);
    setPendingAction(null);
  };

  const actionCopy = pendingAction ? (() => {
    const { type, user } = pendingAction;
    if (type === 'toggle') return {
      title: user.isActive ? 'Deactivate user?' : 'Activate user?',
      message: `${user.username} (${user.email}) will be ${user.isActive ? 'blocked from logging in' : 'allowed to log in again'}.`,
      label: user.isActive ? 'Deactivate User' : 'Activate User',
      tone: user.isActive ? 'danger' : 'success',
    };
    if (type === 'plan') return {
      title: 'Change user plan?',
      message: `${user.username} will be switched from ${user.plan} to ${user.plan === 'pro' ? 'free' : 'pro'}.`,
      label: 'Change Plan',
      tone: 'warning',
    };
    return {
      title: 'Reset password?',
      message: `${user.username} will receive or require a password reset flow.`,
      label: 'Reset Password',
      tone: 'warning',
    };
  })() : null;
  const totalPages = Math.ceil(total / limit);

  const visibleUsers = useMemo(() => users.filter((user) => {
    const matchesPlan = planFilter === 'all' || user.plan === planFilter;
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'active' && user.isActive)
      || (statusFilter === 'inactive' && !user.isActive);
    return matchesPlan && matchesStatus;
  }), [users, planFilter, statusFilter]);

  const activeUsers = users.filter((user) => user.isActive).length;
  const proUsers = users.filter((user) => user.plan === 'pro').length;
  const totalAccounts = users.reduce((sum, user) => sum + Number(user.accountCount || 0), 0);
  const totalTrades = users.reduce((sum, user) => sum + Number(user.tradeCount || 0), 0);

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">{total} users in the current admin view</p>
        </div>
        <div className="relative w-full lg:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by email or username..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-dark-card border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={Users} label="Loaded Users" value={users.length} sub={`${total} total matching search`} />
        <SummaryCard icon={ShieldCheck} label="Active Users" value={activeUsers} sub={`${users.length - activeUsers} inactive`} />
        <SummaryCard icon={Crown} label="Pro Users" value={proUsers} sub={`${users.length - proUsers} free users`} />
        <SummaryCard icon={WalletCards} label="Tracked Accounts" value={totalAccounts} sub={`${totalTrades} trades across loaded rows`} />
      </div>

      <div className="glass-card bg-dark-card p-4 mb-6 flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {['all', 'free', 'pro'].map((plan) => (
            <FilterChip
              key={plan}
              active={planFilter === plan}
              onClick={() => setPlanFilter(plan)}
              label={plan}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {['all', 'active', 'inactive'].map((status) => (
            <FilterChip
              key={status}
              active={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              label={status}
            />
          ))}
        </div>
      </div>

      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-center px-4 py-3">Accounts</th>
                <th className="text-center px-4 py-3">Trades</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Last Login</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : visibleUsers.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">No users match the current filters</td></tr>
              ) : (
                visibleUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-dark-border/50 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-gray-200 font-medium">{user.username}</p>
                        <p className="text-gray-500 text-xs">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        user.plan === 'pro' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700/50 text-gray-400'
                      }`}>
                        {user.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-300">{user.accountCount}</td>
                    <td className="px-4 py-3 text-center text-gray-300">{user.tradeCount}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        user.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{user.createdAt?.split('T')[0]}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{user.lastLogin?.split('T')[0] || 'Never'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setPendingAction({ type: 'toggle', user })}
                          className={`p-1.5 rounded-md transition-all ${user.isActive ? 'hover:bg-red-500/10 text-gray-500 hover:text-red-400' : 'hover:bg-green-500/10 text-gray-500 hover:text-green-400'}`}
                          title={user.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {user.isActive ? <UserX size={14} /> : <UserCheck size={14} />}
                        </button>
                        <button
                          onClick={() => setPendingAction({ type: 'plan', user })}
                          className="p-1.5 rounded-md hover:bg-amber-500/10 text-gray-500 hover:text-amber-400 transition-all"
                          title={`Switch to ${user.plan === 'pro' ? 'free' : 'pro'}`}
                        >
                          <Crown size={14} />
                        </button>
                        <button
                          onClick={() => setPendingAction({ type: 'reset', user })}
                          className="p-1.5 rounded-md hover:bg-blue-500/10 text-gray-500 hover:text-blue-400 transition-all"
                          title="Reset password"
                        >
                          <RotateCcw size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-border">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-md border border-dark-border text-gray-400 hover:text-gray-200 disabled:opacity-30"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-md border border-dark-border text-gray-400 hover:text-gray-200 disabled:opacity-30"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
          <div className="glass-card bg-dark-card w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-dark-border">
              <div>
                <h2 className="text-lg font-bold text-gray-100">{selectedUser.user.username}</h2>
                <p className="text-sm text-gray-500">{selectedUser.user.email}</p>
              </div>
              <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-white/5 rounded-lg">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}
      <AdminConfirmDialog
        open={Boolean(pendingAction)}
        tone={actionCopy?.tone || 'warning'}
        title={actionCopy?.title || ''}
        message={actionCopy?.message || ''}
        confirmLabel={actionCopy?.label || 'Confirm'}
        onConfirm={confirmUserAction}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="glass-card bg-dark-card p-4">
      <div className="flex items-center gap-2 text-gray-500">
        <Icon size={14} />
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-100 mt-2">{value}</p>
      <p className="text-xs text-gray-600 mt-1">{sub}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${
        active
          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
          : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
